import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import _startsWith from 'lodash/startsWith'
import _get from 'lodash/get'
import compose from 'recompose/compose'
import { getBoundActions } from 'actions'
import { connect as connectRedux, useSelector } from 'react-redux'
import { get as getConfig } from 'config'
import FooterEmailCapture from 'components/FooterEmailCapture'
import Menu from 'components/Menu'
import SocialButton, { SOCIAL_BUTTON_TYPES, SOCIAL_BUTTON_SIZES } from 'components/SocialButton'
import AppLinks from 'components/AppLinks'
import LanguageQuickSelect from 'components/LanguageQuickSelect'
import { CONTEXT_TYPE_FOOTER } from 'services/upstream-context'
import { isLiveEvents } from 'services/url'
import { URL_REFER_JOIN } from 'services/url/constants'
import { selectIsGiftCompatible } from 'services/gift/selectors'
import Button, { TYPES } from 'components/Button'
import { getApiIsLoaded as complianceApiIsLoaded, toggleManagementOverlay } from 'services/compliance-management'

const config = getConfig()
const oneTrustEnabled = !!_get(config, ['features', 'oneTrust'])
const EMPTY_LIST = List()

function getFooterMenuId (i) {
  return (`footer__menu-label-${i}`)
}

function renderMenu (props) {
  const {
    menu,
    location,
    staticTextFooter,
    isLoggedIn,
  } = props
  const isGiftCompatible = useSelector(selectIsGiftCompatible)

  return menu.map((menuItem, index) => (
    <div className={getMenuClassName(index)} key={menuItem.get('label')}>
      <div id={getFooterMenuId(index)} className="footer__menu-title">{menuItem.get('label')}</div>
      <Menu
        location={location}
        className="menu-footer"
        itemClassName="menu-item--footer"
        itemLinkClassName="menu-item__link--footer"
        menu={menuItem.get('children', EMPTY_LIST)}
        contextType={CONTEXT_TYPE_FOOTER}
        ariaId={getFooterMenuId(index)}
        isGiftCompatible={isGiftCompatible}
      />
      {
        // this is a bit hacky, but adding the One Trust Cookie Management button
        // this isn't the cleanest, but trying to add a button to a list of links
        // in the menu components would be worse
        index === 0 && oneTrustEnabled && !isLoggedIn
          ? <Button
              className="footer__cookie-settings"
              onClick={() => {
                if (complianceApiIsLoaded()) {
                  toggleManagementOverlay()
                }
              }}
              type={TYPES.LINK}
            >
              {staticTextFooter.getIn(['data', 'cookieSettings'])}
            </Button>
          : null
      }
    </div>
  ))
}

function getMenuClassName (index) {
  switch (index) {
    case 1: return 'footer__menu-second'
    case 2: return 'footer__menu-third'
    default: return 'footer__menu-first'
  }
}

function renderLanguageSelect () {
  return (
    <div className="footer__language-select">
      <LanguageQuickSelect />
    </div>
  )
}

function getClassName (props) {
  const { hasOverlay, location } = props
  const { pathname } = location
  const noMarginPage = _startsWith(pathname, '/manage-profiles')
    || _startsWith(pathname, '/portal')
    || _startsWith(pathname, '/portals')
    || _startsWith(pathname, '/account')
    || isLiveEvents(pathname)
    || _startsWith(pathname, '/share')
    || _startsWith(pathname, '/playlist/anonymous')
  const className = ['footer']
  if (hasOverlay) className.push('footer--mobile-hidden')
  if (noMarginPage) className.push('footer__no-top-margin')
  return className.join(' ')
}

function shouldEmailCapture (location) {
  const { pathname } = location
  return (
    !_startsWith(pathname, '/share')
    && pathname !== URL_REFER_JOIN
    && !_startsWith(pathname, '/portal')
  )
}

function Footer (props) {
  const {
    staticTextFooter,
    location,
    detail,
  } = props
  const { pathname } = location

  return (
    <footer className={getClassName(props)}>
      <div className="footer__content">
        <div className="footer__upper">
          {renderMenu(props)}
          {renderLanguageSelect(props)}
        </div>
        {shouldEmailCapture(location) ? <FooterEmailCapture /> : null}
        <div className="footer__lower">
          <div className="footer__social">
            <div className="footer__social__title">
              {staticTextFooter.getIn(['data', 'connectWithUs'])}
            </div>
            <ul className="footer__social__btns">
              <li>
                <SocialButton
                  size={SOCIAL_BUTTON_SIZES.MEDIUM}
                  type={SOCIAL_BUTTON_TYPES.FACEBOOK}
                  contentType={detail.getIn(['data', 'type', 'content'], null)}
                  pathName={pathname}
                  eventTrackingType={props.eventTrackingType}
                />
              </li>
              <li>
                <SocialButton
                  size={SOCIAL_BUTTON_SIZES.MEDIUM}
                  type={SOCIAL_BUTTON_TYPES.TWITTER}
                  contentType={detail.getIn(['data', 'type', 'content'], null)}
                  pathName={pathname}
                  eventTrackingType={props.eventTrackingType}
                />
              </li>
              <li>
                <SocialButton
                  size={SOCIAL_BUTTON_SIZES.MEDIUM}
                  type={SOCIAL_BUTTON_TYPES.INSTAGRAM}
                  contentType={detail.getIn(['data', 'type', 'content'], null)}
                  pathName={pathname}
                  eventTrackingType={props.eventTrackingType}
                />
              </li>
              <li>
                <SocialButton
                  size={SOCIAL_BUTTON_SIZES.MEDIUM}
                  type={SOCIAL_BUTTON_TYPES.YOUTUBE}
                  contentType={detail.getIn(['data', 'type', 'content'], null)}
                  pathName={pathname}
                  eventTrackingType={props.eventTrackingType}
                />
              </li>
              <li>
                <SocialButton
                  size={SOCIAL_BUTTON_SIZES.MEDIUM}
                  type={SOCIAL_BUTTON_TYPES.PINTEREST}
                  contentType={detail.getIn(['data', 'type', 'content'], null)}
                  pathName={pathname}
                  eventTrackingType={props.eventTrackingType}
                />
              </li>
            </ul>
          </div>
          <AppLinks label={staticTextFooter.getIn(['data', 'gaiaApps'])} />
        </div>
      </div>
    </footer>
  )
}

Footer.propTypes = {
  location: PropTypes.object.isRequired,
  menu: ImmutablePropTypes.list.isRequired,
  detail: ImmutablePropTypes.map.isRequired,
  staticTextFooter: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectRedux(
    (state) => ({
      auth: state.auth,
      detail: state.detail,
      isLoggedIn: !!state.auth.get('jwt'),
      menu: state.menu.getIn(['data', 'footer'], EMPTY_LIST),
      hasOverlay: state.header.get('navigationOverlayVisible'),
      staticTextFooter: state.staticText.getIn(['data', 'footer'], Map()),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        renderModal: actions.dialog.renderModal,
      }
    },
  ),
)(Footer)
